import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMeasure } from 'react-use';
import { Step, StepList, StepPanel, Steps } from '../CommonComponents/OPL/Stepper';
import { ScrollHereOnce } from '../CommonComponents/ScrollHereOnce';
import useScript from '../_hooks/useScript';
import { Text } from '../CommonComponents/OPL/Text/Text';
import { TruncatedText } from '../CommonComponents/OPL/Text/TruncatedText';
import { Card } from '../CommonComponents/OPL/Card/Card';
import { StickyFooter } from '../CommonComponents/OPL/StickyFooter/StickyFooter';
import { Button } from '../CommonComponents/OPL/Button/Button';
import { useResetModuleWrapperStyles } from '../_hooks/useResetModuleWrapperStyles';
import { Box } from '../CommonComponents/OPL/Box/Box';
import { selectComponentProps } from '../ConsumerArea/ConsumerAreaSelector';
import {
  ApiService,
  ComponentProps,
  LAYOUT_VARS,
  PAGE_DESCRIPTION,
  Status,
  UserConfig,
} from './CustomFormsWorkflow.meta';
import { CustomFormsComponent } from './components/CustomFormsComponent/CustomFormsComponent';
import { FullPageLoading } from '../CommonComponents/OPL/FullpageLoading/FullPageLoading';
import { ErrorState } from '../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { EmptyState } from '../CommonComponents/OPL/AsyncStates/EmptyState/EmptyState';
import { Collection, Content, Main, ScrollableWrapper } from './CustomFormsWorkflow.styles';
import { ReviewPage } from './components/ReviewPage/ReviewPage';
import { logout } from '../Admin/utils/logout';
import { useResetRootFontSize } from './hooks/useResetRootFontSize';
import { Icon } from '../CommonComponents/OPL/Icon/Icon';
import { PercentageComplete } from './components/PercentageComplete/PercentageComplete';
import { LegacyVoyaWorkflow } from './LegacyVoyaWorkflow/LegacyVoyaWorkflow';

type WorkflowPosition =
  | { type: 'filling'; collectionIndex: number; stepIndex: number }
  | { type: 'review' };

function getCollectionIcon(type: 'Account' | 'Household' | 'Contact' | null): string {
  switch (type) {
    case 'Account':
      return 'fas fa-landmark';
    case 'Contact':
      return 'far fa-user';
    case 'Household':
      return 'fas fa-home';
    default:
      return 'far fa-clipboard-user';
  }
}

export const CustomFormsWorkflow = () => {
  useResetModuleWrapperStyles();
  useResetRootFontSize();

  const componentProps: ComponentProps = useSelector(selectComponentProps);

  if (componentProps.IsVoya) {
    return <LegacyVoyaWorkflow />;
  }

  if (componentProps.CustomFormsData) {
    return <CustomFormsWorkflowContent />;
  }

  return null;
};

const CustomFormsWorkflowContent = () => {
  const componentProps: ComponentProps = useSelector(selectComponentProps);

  const { CustomFormsData } = componentProps;

  const [position, setPosition] = useState<WorkflowPosition>({
    type: 'filling',
    collectionIndex: 0,
    stepIndex: 0,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isWorkflowCompleted, setIsWorkflowCompleted] = useState(false);

  const [apiService, setApiService] = useState<ApiService | null>(null);

  const navigate = (newPosition: WorkflowPosition) => {
    setApiService(null);
    setPosition(newPosition);
  };

  const [collectionStatuses, setCollectionStatuses] = useState<Status[]>(() =>
    CustomFormsData.collections.map(() => 'Active'),
  );

  const [isScriptLoaded, isScriptError] = useScript(componentProps.ComponentPath, {
    isModule: true,
  });

  const [headerRef, { height: headerHeight }] = useMeasure<HTMLDivElement>();
  const [stepListRef, { height: stepListHeight }] = useMeasure<HTMLDivElement>();

  const percentageCompleted = useMemo(() => {
    if (position.type === 'review') {
      return 100;
    }

    const totalSessionsCount = CustomFormsData.collections.reduce(
      (sum, collection) => sum + collection.sessions.length,
      0,
    );

    if (totalSessionsCount === 0) {
      return 0;
    }

    const currentSessionCount =
      CustomFormsData.collections
        .slice(0, position.collectionIndex)
        .reduce((sum, collection) => sum + collection.sessions.length, 0) + position.stepIndex;

    return Math.round((currentSessionCount / totalSessionsCount) * 100);
  }, [CustomFormsData.collections, position]);

  if (!isScriptLoaded) {
    return (
      <ScrollableWrapper>
        <Box style={{ height: '100vh' }}>
          <FullPageLoading text="Loading" />
        </Box>

        <StickyFooter />
      </ScrollableWrapper>
    );
  }

  if (isScriptError) {
    return (
      <ScrollableWrapper>
        <Box style={{ height: '100vh' }}>
          <ErrorState
            height="100%"
            message="There was an error loading the page. Please try again later."
          />
        </Box>

        <StickyFooter />
      </ScrollableWrapper>
    );
  }

  if (CustomFormsData.collections.length === 0) {
    return (
      <ScrollableWrapper>
        <Box style={{ height: '100vh', marginTop: -72 }}>
          <EmptyState height="100%" />
        </Box>

        <StickyFooter />
      </ScrollableWrapper>
    );
  }

  const userConfig: UserConfig = {
    oasToken: componentProps.UserConfig.OasToken,
    viewType: componentProps.UserConfig.ViewType,
    env: componentProps.UserConfig.Env,
  };

  const currentCollection =
    position.type === 'filling' ? CustomFormsData.collections[position.collectionIndex] : undefined;

  const currentSession =
    position.type === 'filling' && currentCollection
      ? currentCollection.sessions[position.stepIndex]
      : undefined;

  const withUnsavedChangesGuard = (callback: () => void) => {
    if (apiService?.confirmUnsavedChangesIfDirty) {
      apiService.confirmUnsavedChangesIfDirty(callback);
    } else {
      callback();
    }
  };

  const onFormReady = (evtDetail: { service: ApiService }) => {
    setApiService(evtDetail.service);

    if (currentSession?.readOnly) {
      evtDetail.service.options({ showAsPreview: true });
    }
  };

  const goToPreviousStep = () => {
    withUnsavedChangesGuard(() => {
      if (position.type !== 'filling') {
        return;
      }

      if (position.collectionIndex > 0 && position.stepIndex === 0) {
        const prevCollection = CustomFormsData.collections[position.collectionIndex - 1];
        navigate({
          type: 'filling',
          collectionIndex: position.collectionIndex - 1,
          stepIndex: prevCollection.sessions.length - 1,
        });
        return;
      }

      navigate({ ...position, stepIndex: position.stepIndex - 1 });
    });
  };

  const goToNextStep = () => {
    if (position.type !== 'filling') {
      return;
    }

    const collection = CustomFormsData.collections[position.collectionIndex];
    const isLastSession = position.stepIndex === collection.sessions.length - 1;
    const isLastCollection = position.collectionIndex === CustomFormsData.collections.length - 1;

    if (isLastSession) {
      setCollectionStatuses(statuses =>
        statuses.map((status, index) =>
          index === position.collectionIndex ? 'Completed' : status,
        ),
      );

      if (isLastCollection) {
        setIsWorkflowCompleted(true);
        navigate({ type: 'review' });
      } else {
        navigate({
          type: 'filling',
          collectionIndex: position.collectionIndex + 1,
          stepIndex: 0,
        });
      }
    } else {
      navigate({
        type: 'filling',
        collectionIndex: position.collectionIndex,
        stepIndex: position.stepIndex + 1,
      });
    }
  };

  const submitForm = async () => {
    if (currentSession?.readOnly) {
      goToNextStep();
      return;
    }

    if (apiService) {
      setIsSubmitting(true);

      const resp = await apiService.save();

      setIsSubmitting(false);

      if (resp && !resp.hasErrors) {
        goToNextStep();
      }
    }
  };

  const selectCollection = (index: number) => {
    if (position.type === 'filling' && index === position.collectionIndex) {
      return;
    }

    withUnsavedChangesGuard(() => {
      navigate({ type: 'filling', collectionIndex: index, stepIndex: 0 });
    });
  };

  return (
    <ScrollableWrapper>
      <ScrollHereOnce
        key={
          position.type === 'review'
            ? 'review'
            : `${position.collectionIndex}-${position.stepIndex}`
        }
        options={false}
      />

      <Box px="48">
        <Box ref={headerRef} mb="24">
          <Text as="h1" fontSize="h6" fontWeight={600}>
            {CustomFormsData.title}
          </Text>

          <Text as="p" color="neutral.500" fontSize="small" maxWidth={700}>
            {CustomFormsData.subTitle || PAGE_DESCRIPTION}
          </Text>
        </Box>

        <Main headerHeight={headerHeight} style={{ gap: LAYOUT_VARS.gridGap }}>
          <Card
            width={LAYOUT_VARS.sidebarWidth}
            alignSelf="flex-start"
            display="flex"
            flexDirection="column"
            css="gap: 16px; max-height: 100%"
            p="16"
          >
            <Box css="overflow-y: auto; min-height: 0; flex: 1">
              <Box display="grid" css="grid-auto-rows: 1fr; gap: 16px">
                {CustomFormsData.collections.map((collection, index) => (
                  <Collection
                    key={collection.id}
                    role="button"
                    tabIndex={isWorkflowCompleted ? 0 : undefined}
                    isCompleted={collectionStatuses[index] === 'Completed'}
                    onClick={() => {
                      if (!isWorkflowCompleted) {
                        return;
                      }

                      selectCollection(index);
                    }}
                    onKeyDown={event => {
                      if (!isWorkflowCompleted) {
                        return;
                      }

                      if (event.key === 'Enter') {
                        selectCollection(index);
                      }

                      if (event.key === ' ') {
                        event.preventDefault();
                        selectCollection(index);
                      }
                    }}
                    aria-current={position.type === 'filling' && position.collectionIndex === index}
                    aria-disabled={!isWorkflowCompleted}
                  >
                    <Icon
                      className={getCollectionIcon(collection.type)}
                      color="neutral.500"
                      css="flex-shrink: 0"
                    />

                    <Box flex={1} display="flex" flexDirection="column">
                      <Text fontWeight={600} color="neutral.600">
                        {collection.title}
                      </Text>

                      {collection.subTitle && (
                        <TruncatedText color="neutral.500" style={{ fontSize: '11px' }}>
                          {collection.subTitle}
                        </TruncatedText>
                      )}
                    </Box>

                    <Box display="flex" alignItems="center" css="gap: 8px; flex-shrink: 0">
                      {collectionStatuses[index] === 'Completed' && (
                        <Icon className="fas fa-check-circle" color="success.500" />
                      )}

                      {isWorkflowCompleted && (
                        <Icon className="fas fa-chevron-right" color="neutral.400" ml={2} />
                      )}
                    </Box>
                  </Collection>
                ))}
              </Box>
            </Box>

            <PercentageComplete
              percentageCompleted={isWorkflowCompleted ? 100 : percentageCompleted}
            />
          </Card>

          <Card flex={1} p="24">
            {position.type === 'review' ? (
              <Box mt="24">
                <ReviewPage />
              </Box>
            ) : (
              <>
                <Steps
                  selectedIndex={position.stepIndex}
                  onSelect={index => {
                    if (index === position.stepIndex) {
                      return;
                    }

                    withUnsavedChangesGuard(() => {
                      navigate({ ...position, stepIndex: index });
                    });
                  }}
                  css="height: 100%"
                >
                  <Box ref={stepListRef}>
                    <TruncatedText fontSize="h6" fontWeight={500} mb="8">
                      {CustomFormsData.collections[position.collectionIndex].title}
                    </TruncatedText>

                    <StepList>
                      {CustomFormsData.collections[position.collectionIndex].sessions.map(
                        (session, index) => (
                          <Step
                            key={session.id}
                            disabled={!isWorkflowCompleted && position.stepIndex < index}
                          >
                            {session.title}
                          </Step>
                        ),
                      )}
                    </StepList>
                  </Box>

                  <Content stepListHeight={stepListHeight} mt={LAYOUT_VARS.gridGap}>
                    {CustomFormsData.collections[position.collectionIndex].sessions.map(
                      (session, index) => (
                        <StepPanel
                          key={session.id}
                          css={index === position.stepIndex ? `height: 100%` : undefined}
                        >
                          <CustomFormsComponent
                            userConfig={userConfig}
                            formSessionId={session.id}
                            onFormReady={onFormReady}
                          />
                        </StepPanel>
                      ),
                    )}
                  </Content>
                </Steps>
              </>
            )}
          </Card>
        </Main>
      </Box>

      <StickyFooter fullWidthChildren>
        {position.type === 'review' ? (
          <>
            <Button
              variant="secondary"
              leftIcon="far fa-pencil"
              size="large"
              onClick={() => navigate({ type: 'filling', collectionIndex: 0, stepIndex: 0 })}
              mr="auto"
              ml={LAYOUT_VARS.sidebarWidth + LAYOUT_VARS.pagePadding + LAYOUT_VARS.gridGap}
            >
              Edit
            </Button>

            <Button
              size="large"
              leftIcon="fas fa-check"
              onClick={() => logout(true)}
              ml="auto"
              mr={LAYOUT_VARS.pagePadding}
            >
              Done
            </Button>
          </>
        ) : (
          <>
            {(position.stepIndex > 0 || position.collectionIndex > 0) && (
              <Button
                variant="secondary"
                leftIcon="fas fa-chevron-left"
                size="large"
                onClick={goToPreviousStep}
                disabled={isSubmitting}
                mr="auto"
                ml={LAYOUT_VARS.sidebarWidth + LAYOUT_VARS.pagePadding + LAYOUT_VARS.gridGap}
              >
                Previous
              </Button>
            )}

            <Button
              rightIcon="fas fa-chevron-right"
              size="large"
              onClick={submitForm}
              disabled={isSubmitting || !apiService}
              ml="auto"
              mr={LAYOUT_VARS.pagePadding}
            >
              Next
            </Button>
          </>
        )}
      </StickyFooter>
    </ScrollableWrapper>
  );
};
